// @ts-check

/**
 * @file Configures the Expo root layout and startup gates.
 */

import {PortalHost, PortalProvider} from "conjointment"
import {Slot, Stack, useRouter} from "expo-router"
import {DefaultTheme, ThemeProvider} from "@react-navigation/native"
import {memo, useEffect} from "react"
import OutsideEyeProvider from "outside-eye/build/provider.js"
import {Platform} from "react-native"
import WithExpoLocationPath from "on-location-changed/build/with-expo-location-path"
import {SafeAreaProvider} from "react-native-safe-area-context"
import {ActivityIndicator, Text, View} from "react-native-propforge"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import useDatabase from "velocious/build/src/database/use-database.js"
import useSystemTest from "system-testing/build/use-system-test.js"

import "../src/config/configuration"
import BoardLayout from "../src/components/board-layout"
import ConnectionErrorScreen from "../src/auth/connection-error-screen"
import Option from "../src/models/option"
import useCurrentUser, {clearCurrentUser, WithCurrentUser} from "../src/auth/use-current-user"
import {clearSessionToken, hydrateSessionToken} from "../src/shared/session-option"
import {performanceMeasure, performanceNow} from "../src/shared/performance-debug"
import {projectSyncLifecycle} from "../src/sync/project-sync-lifecycle"
import initializeTensorBuzzBugReporting from "../src/shared/tensorbuzz-bug-reporting"
import initializeTranslations from "../src/translations/initialize"
import initializeVelocious, {teardownVelocious} from "../src/velocious/initialize"
import NativeSystemTestBrowserHelper from "../src/system-tests/native-system-test-browser-helper"
import registerBackendModelClassesForSystemTests from "../src/system-tests/register-backend-model-classes"
import {expoPublicEnv, expoPublicEnvFlag} from "../src/shared/expo-public-env"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @import SystemTestBrowserHelper from "system-testing/build/system-test-browser-helper.js" */

// React Navigation's `DefaultTheme.colors.background` is the iOS-style
// `rgb(242, 242, 242)` light-grey that native-stack paints onto every
// screen wrapper. Override it to `"transparent"` so the single painted
// surface (`boardLayout/content`'s `colors.screen`) is the only thing
// that carries the body background, and the active theme — light or
// dark — actually reaches the user. The rest of the palette is left at
// its default since we don't otherwise consume `theme.colors`; what
// matters is that the per-screen card stops painting its own grey.
const transparentNavigationTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    background: "transparent"
  }
}
/** @import {RequireMigrationContextType} from "velocious/build/src/database/migrator/types.js" */
/** @type {Record<string, object>} */
const styles = {}
const rootStartupStartedAt = performanceNow()
const systemTestEnabled = expoPublicEnvFlag("EXPO_PUBLIC_SYSTEM_TEST") || undefined
const systemTestHost = expoPublicEnv("EXPO_PUBLIC_SYSTEM_TEST_HOST") || undefined
const nativeSystemTestEnabled = systemTestEnabled && Boolean(expoPublicEnv("EXPO_PUBLIC_SYSTEM_TEST_CLIENT_WS_PORT"))

// Initialize the Velocious frontend-model transport once, at module load,
// so every screen — including the sign-in screen and BoardLayout's
// currentProfile call — has a configured backend URL before the first
// render. Calling this from inside setup() would fire on every render;
// module scope runs it exactly once per page load.
initializeVelocious()
initializeTranslations()
initializeTensorBuzzBugReporting()
hideExpoLogBoxToastForSystemTests()

/**
 * Suppresses Expo's dev-only LogBox div `id="error-toast"` overlay
 * during system-test runs. The toast is positioned at the bottom of
 * the viewport and intercepts clicks on real UI as soon as any
 * browser-console error fires. Production builds don't ship the
 * LogBox, so this is purely a dev-server-vs-system-test fix.
 * @returns {void} Nothing.
 */
function hideExpoLogBoxToastForSystemTests() {
  if (typeof window === "undefined" || !window.location) return
  const params = new URLSearchParams(window.location.search)
  if (params.get("systemTest") !== "true") return
  if (typeof document === "undefined") return
  const styleId = "system-test-hide-expo-error-toast"
  if (document.getElementById(styleId)) return
  const styleElement = document.createElement("style")
  styleElement.id = styleId
  styleElement.textContent = "#error-toast { display: none !important; pointer-events: none !important; }"
  document.head.appendChild(styleElement)
}

/**
 * Emits focused browser-side system-test diagnostics.
 * @param {string} message Short event label.
 * @param {Record<string, unknown>} [details] Structured fields for the event.
 * @returns {void} Nothing.
 */
function systemTestDebugLog(message, details = {}) {
  if (typeof window === "undefined" || !window.location) return
  if (!window.location.search.includes("systemTestDebug=true")) return
  console.log("[awesome_tasks system-test]", message, {
    href: window.location.href,
    ...details
  })
}

/**
 * Module-level deferred that resolves when the local Velocious database
 * has finished migrating and the session token has been hydrated into
 * memory. The system-test `onInitialize` callback awaits it before
 * calling `Option.destroyAll()` — the callback can fire before the
 * component's DB-loading useEffect resolves, and touching the Option
 * model before migrations have run throws "no such table".
 * @type {(value?: void) => void}
 */
let resolveAppReady = () => {}
const appReadyPromise = new Promise((resolve) => { resolveAppReady = resolve })

/**
 * Loads local Velocious migration modules.
 * @returns {Promise<RequireMigrationContextType>} Metro migration context.
 */
async function migrationsRequireContextCallback() {
  // @ts-expect-error — Metro-specific require.context is not in Node's require type.
  return require.context("../src/database/migrations/", true, /\.\/(\d{14})-(.+)\.js$/)
}

/**
 * Full-screen loading indicator shown while the app finishes starting
 * up. Uses a literal English label because startup locale resolution waits
 * until after the current user has loaded.
 * @returns {ReactNode} Startup loading screen.
 */
function LoadingScreen() {
  return (
    <View
      style={styles.loadingScreen ||= {
        alignItems: "center",
        backgroundColor: "#f8fafc",
        flex: 1,
        gap: 12,
        justifyContent: "center"
      }}
      testID={nativeSystemTestEnabled ? "systemTestingComponent" : "rootLayout/loading"}
    >
      <ActivityIndicator color="#1d4ed8" size="large" />
      <Text
        style={styles.loadingText ||= {
          color: "#475569",
          fontSize: 15,
          fontWeight: 600
        }}
        testID="rootLayout/loading/label"
      >
        Loading...
      </Text>
    </View>
  )
}

/**
 * @typedef {object} RequireCurrentUserLoadedProps
 * @property {ReactNode} children Wrapped app content.
 */

/**
 * Gate below WithCurrentUser that blocks the app until the first
 * `currentProfile()` fetch has resolved. Without this, BoardLayout
 * would render on the first tick with `{loaded: false, user: null}`
 * and flash the sign-in form for a signed-in user on cold load.
 * If the boot fetch fails (backend unreachable, network error), the
 * gate renders a connection-error screen instead of falling through
 * to the sign-in form, which can't sign anyone in without the
 * backend either.
 * @param {RequireCurrentUserLoadedProps} props Wrapped content.
 * @returns {ReactNode} Wrapped content, loading screen, or connection-error screen.
 */
function RequireCurrentUserLoaded({children}) {
  const {connectionError, loaded} = useCurrentUser()
  if (!loaded) return <LoadingScreen />
  if (connectionError) return <ConnectionErrorScreen error={connectionError} />
  return children
}

/** @typedef {{[key: string]: unknown}} RootLayoutProps */

/**
 * @typedef {object} RootLayoutState
 * @property {boolean} sessionHydrated Whether the session token has been loaded.
 */

/**
 * Frontend Expo router layout for the migration scaffold.
 * @augments {ShapeComponent<RootLayoutProps, RootLayoutState>}
 */
class RootLayout extends ShapeComponent {
  databaseLoaded = false
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {SystemTestBrowserHelper | null} */
  systemTestBrowserHelper = null
  /** @type {NativeSystemTestBrowserHelper | null} */
  nativeSystemTestBrowserHelper = null
  /** @type {RootLayoutState} */
  state = {
    sessionHydrated: false
  }

  /** Initializes router, database, system-test hooks, and session hydration. */
  setup() {
    const router = useRouter()
    const {loaded: databaseLoaded} = useDatabase({migrationsRequireContextCallback})
    const {systemTestBrowserHelper} = useSystemTest({
      browserHelper: this.tt.systemTestBrowserHelperOverride(),
      enabled: systemTestEnabled,
      host: systemTestHost,
      onDismissTo: this.tt.onSystemTestDismissTo,
      onFirstInitialize: this.tt.onSystemTestFirstInitialize,
      onInitialize: this.tt.onSystemTestInitialize,
      onNavigate: this.tt.onSystemTestNavigate,
      onTeardown: teardownVelocious
    })

    this.databaseLoaded = databaseLoaded
    this.router = router
    this.systemTestBrowserHelper = systemTestBrowserHelper

    useEffect(() => {
      if (!databaseLoaded) return

      const sessionHydrationStartedAt = performanceNow()
      void hydrateSessionToken().then(() => {
        this.s.sessionHydrated = true
        resolveAppReady()
        performanceMeasure("rootLayout.sessionHydrated", sessionHydrationStartedAt)
        performanceMeasure("rootLayout.ready", rootStartupStartedAt)
      })
    }, [databaseLoaded])
    useEffect(() => {
      if (!systemTestBrowserHelper) return

      void registerBackendModelClassesForSystemTests(systemTestBrowserHelper)
    }, [systemTestBrowserHelper])
  }

  /**
   * Builds a native system-test helper when the APK carries embedded bridge ports.
   * @returns {NativeSystemTestBrowserHelper | undefined} Native helper override.
   */
  systemTestBrowserHelperOverride() {
    if (!nativeSystemTestEnabled) return undefined

    if (!this.nativeSystemTestBrowserHelper) {
      this.nativeSystemTestBrowserHelper = new NativeSystemTestBrowserHelper()
    }

    return this.nativeSystemTestBrowserHelper
  }

  /**
   * Replaces the current path for system-test dismissal.
   * @param {{path: string}} args Navigation payload.
   * @returns {void} Nothing.
   */
  onSystemTestDismissTo = ({path}) => {
    systemTestDebugLog("dismissTo", {path})
    this.router.replace(path)
  }

  /**
   * Registers browser Scoundrel classes before the first system-test callback runs.
   * @returns {Promise<void>} Resolves after registration finishes.
   * @rejects {Error} When no browser helper is available.
   */
  onSystemTestFirstInitialize = async () => {
    const {systemTestBrowserHelper} = this.tt

    if (!systemTestBrowserHelper) {
      throw new Error("System-test browser helper is missing before model registration")
    }

    await registerBackendModelClassesForSystemTests(systemTestBrowserHelper)
  }

  /**
   * Clears local state before a system-test run.
   * @returns {Promise<void>} Resolves when system-test state is reset.
   */
  onSystemTestInitialize = async () => {
    // The test harness can fire "initialize" before the local Velocious
    // database has finished migrating — touching Option before the
    // `options` table exists throws. Wait until the root layout's
    // DB + session hydration has completed.
    await appReadyPromise

    // Each system-test spec starts from a clean local Velocious database.
    // Stop and drain authenticated sync before removing its session/catalog
    // ownership, matching the production sign-out ordering.
    await projectSyncLifecycle.stop()

    // Clear state so the previous test's current session token and anything
    // else we add to Option later cannot bleed across specs. The
    // `clearSessionToken()` call also zeros the in-memory token cache
    // so `Option.destroyAll()` doesn't leave the next request still
    // sending the previous test's token header.
    await Option.destroyAll()
    await clearSessionToken()
    clearCurrentUser()
  }

  /**
   * Navigates to a path for system tests.
   * @param {{path: string}} args Navigation payload.
   * @returns {void} Nothing.
   */
  onSystemTestNavigate = ({path}) => {
    systemTestDebugLog("navigate", {path})
    this.router.navigate(path)
  }

  /**
   * Renders app providers and router stack.
   * @returns {ReactNode} Root provider tree.
   */
  render() {
    const {sessionHydrated} = this.s

    if (!this.databaseLoaded || !sessionHydrated) return <LoadingScreen />

    return (
      <SafeAreaProvider
        style={styles.safeAreaProvider ||= {
          flex: 1
        }}
        testID="rootLayout/safeAreaProvider"
      >
        <OutsideEyeProvider>
          <PortalProvider>
            <WithCurrentUser>
              <RequireCurrentUserLoaded>
                <PortalHost name="base" placement="above">
                  <WithExpoLocationPath>
                    <BoardLayout>
                      {Platform.OS === "web" &&
                        <ThemeProvider value={transparentNavigationTheme}>
                          <Stack screenOptions={{headerShown: false}} />
                        </ThemeProvider>
                      }
                      {Platform.OS !== "web" &&
                        // react-native-screens' Screen always fills its parent
                        // instead of growing with its content, so BoardLayout's
                        // body ScrollView gets zero scroll range around a native
                        // <Stack> and everything below the fold becomes
                        // unreachable. <Slot /> renders the matched route as
                        // plain views that extend the scroll content like web.
                        <Slot />
                      }
                    </BoardLayout>
                  </WithExpoLocationPath>
                </PortalHost>
              </RequireCurrentUserLoaded>
            </WithCurrentUser>
          </PortalProvider>
        </OutsideEyeProvider>
      </SafeAreaProvider>
    )
  }
}

/** @type {NamedExoticComponent<RootLayoutProps>} */
const RootLayoutComponent = memo(shapeComponent(RootLayout))

export default RootLayoutComponent
